import { useState } from "react";
import { DownloadSimpleIcon } from "@phosphor-icons/react/DownloadSimple";
import { PackageIcon } from "@phosphor-icons/react/Package";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlBarItem } from "@freightsimple/ui-core/PageLayout/ControlBarItem";
import { DateRangePicker } from "@freightsimple/ui-core/DateTime/Controls/DateRangePicker";
import { Dropdown } from "@freightsimple/ui-core/Controls/Dropdown";
import { EmptyState } from "@freightsimple/ui-core/Display/EmptyState";
import { Page } from "@freightsimple/ui-core/PageLayout/Page";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { SearchInput } from "@freightsimple/ui-core/Controls/SearchInput";
import { T } from "@freightsimple/ui-core/Typography/T";
import { toast } from "sonner";
import { ShipmentStatusFilter } from "@freightsimple/generated-carriers-openapi-client/models/ShipmentStatusFilter";
import { carriersApi } from "@/App/Auth/carrierApi";
import { ShipmentsTable } from "@/App/Tables/ShipmentsTable";
import { CarrierLoadFailed } from "@/App/CarrierLoadFailed";
import { useCarrierFetch } from "@/App/useCarrierFetch";
import { downloadBase64File } from "@/App/Opportunities/downloadBase64File";
import { CarrierPageLogo } from "@/App/CarrierPageLogo";
import {
  EMPTY_SHIPMENT_FILTERS,
  SHIPMENT_STATUS_FILTERS,
  describeShowing,
  isFiltering,
  toShipmentQuery,
  type ShipmentFilters,
} from "@/App/Views/shipmentsFiltering";

const PAGE_SIZE = 50;

export function ShipmentsView() {
  const [filters, setFilters] = useState<ShipmentFilters>(
    EMPTY_SHIPMENT_FILTERS,
  );
  const [page, setPage] = useState(0);
  const [exporting, setExporting] = useState(false);

  const query = toShipmentQuery(filters);
  const queryKey = `${JSON.stringify(query)}|${page}`;

  const { state, refreshing, refresh } = useCarrierFetch(
    () =>
      carriersApi().getCarrierShipments({
        ...query,
        page,
        pageSize: PAGE_SIZE,
      }),
    queryKey,
  );

  // Any filter change starts again at the first page — staying on page 4 of a
  // different result set shows an empty screen for no reason.
  function update(next: Partial<ShipmentFilters>) {
    setFilters({ ...filters, ...next });
    setPage(0);
  }

  async function exportCsv() {
    setExporting(true);
    try {
      const file = await carriersApi().exportCarrierShipments(query);
      downloadBase64File(file.fileName, file.contentType, file.base64Content);
    } catch {
      toast.error("Couldn't build the export. Please try again.");
    } finally {
      setExporting(false);
    }
  }

  const data = state.status === "loaded" ? state.data : undefined;
  const filtering = isFiltering(filters);

  return (
    <Page
      leadingVisual={<CarrierPageLogo />}
      icon={PackageIcon}
      title="Shipments"
      description="Shipments you've been booked on with FreightSimple."
      fullBleed
      headerStats={
        data
          ? [
              {
                label: "Showing",
                value: describeShowing(
                  data.shipments.length,
                  data.totalCount,
                  data.page,
                  data.pageSize,
                ),
              },
            ]
          : undefined
      }
      refreshing={refreshing}
      headerActions={
        <Button
          icon={DownloadSimpleIcon}
          onClick={exportCsv}
          loading={exporting}
          tooltip="Download every shipment matching these filters"
        >
          Export
        </Button>
      }
      filterBar={
        <>
          <ControlBarItem label="Search">
            <SearchInput
              value={filters.search}
              onChange={(search) => update({ search })}
              placeholder="PRO, BOL, reference, city…"
              width="lg"
            />
          </ControlBarItem>
          <ControlBarItem label="Status">
            <Dropdown
              options={SHIPMENT_STATUS_FILTERS}
              value={filters.status}
              onChange={(status) =>
                update({ status: status as ShipmentStatusFilter })
              }
              width="md"
            />
          </ControlBarItem>
          <ControlBarItem label="Pickup Dates">
            <DateRangePicker
              value={filters.dates}
              onChange={(dates) => update({ dates })}
              allowClear
            />
          </ControlBarItem>
        </>
      }
    >
      {state.status === "loading" && (
        <T size="sm" color="secondary">
          Loading…
        </T>
      )}
      {state.status === "error" && (
        <CarrierLoadFailed what="your shipments" onRetry={refresh} />
      )}
      {data && data.shipments.length === 0 && (
        <EmptyState
          icon={PackageIcon}
          title={filtering ? "No shipments match" : "No shipments yet"}
          description={
            filtering
              ? "Try a different reference, or widen the dates."
              : "Shipments you're booked on will appear here."
          }
        />
      )}
      {data && data.shipments.length > 0 && (
        <>
          <ShipmentsTable shipments={data.shipments} />
          <Pager_
            page={data.page}
            pageSize={data.pageSize}
            total={data.totalCount}
            onPage={setPage}
          />
        </>
      )}
    </Page>
  );
}

/**
 * Explicit page controls rather than infinite scroll: carriers come here with a
 * reference in hand and reconcile against their own system, and both of those
 * want a stated position in a stated total.
 */
function Pager_({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);
  if (lastPage === 0) return undefined;

  return (
    <Row gap={2} justify="between">
      <Button
        disabled={page === 0}
        disabledMessage="You're on the first page"
        onClick={() => onPage(page - 1)}
      >
        Previous
      </Button>
      <T size="sm" color="secondary">
        Page {page + 1} of {lastPage + 1}
      </T>
      <Button
        disabled={page >= lastPage}
        disabledMessage="You're on the last page"
        onClick={() => onPage(page + 1)}
      >
        Next
      </Button>
    </Row>
  );
}
